import {
  createEffect,
  createMemo,
  createResource,
  createSignal,
  For,
  Show,
} from "solid-js";
import { A, useSearchParams } from "@solidjs/router";
import type {
  DeviceInfo,
  MeasurementPoint,
  PointReading,
  SiteNode,
} from "@energyplatform/client";
import { api } from "../services.ts";
import { locale, t } from "../i18n.ts";
import { formatAgo } from "../format.ts";
import {
  depthFirst,
  pathTo,
  reloadTree,
  scope,
  setScope,
  siteTree,
} from "../context.ts";
import { subtreeOf } from "../tree.ts";
import {
  dataState,
  isFiltered,
  passes,
  pointState,
  sameFilter,
  unassignedDevices,
  viewFilter,
  visibleNodes,
  VIEWS,
  type Filter,
  type PointState,
} from "../assets.ts";
import { Icon } from "../icons.tsx";
import {
  MEDIA_IDS,
  MediumBadge,
  NODE_KINDS,
  NodeKindIcon,
} from "../components/Media.tsx";
import { ErrorBox } from "../components/ErrorBox.tsx";
import { PlaceDeviceDialog } from "../components/PlaceDevice.tsx";
import {
  DataChip,
  PointCard,
  READINESS_CHIP,
  ReadingValue,
} from "../components/PointCard.tsx";
import { LocationPicker, type LatLon } from "../components/LocationPicker.tsx";
import {
  ConfirmDialog,
  Dialog,
  operator,
  PageHeader,
  Skeleton,
  useAction,
} from "../components/ui.tsx";

const ROLES = [
  "main",
  "source",
  "storage",
  "sub",
  "load",
  "environment",
] as const;
const READINESS = [
  "project",
  "config",
  "check",
  "running",
  "paused",
  "retired",
] as const;

type Selection = { kind: "node"; id: string } | { kind: "point"; id: string };

/**
 * Assets (doc 12, prototype screen "Zasoby"): the tree of places with their measurement points and their state,
 * filters and a flat list of points, devices from integrations that no point uses yet, and the details of the chosen
 * place or point. A point keeps its history when the device is swapped.
 */
export function AssetsPage() {
  const [params, setParams] = useSearchParams<{ point?: string }>();
  const [selection, setSelection] = createSignal<Selection | undefined>(
    params.point ? { kind: "point", id: params.point } : undefined,
  );
  createEffect(() => {
    if (params.point) {
      setSelection({ kind: "point", id: params.point });
    }
  });
  const select = (s: Selection | undefined) => {
    setSelection(s);
    if (params.point && s?.id !== params.point) {
      setParams({ point: undefined }, { replace: true });
    }
  };

  const [nodeDialog, setNodeDialog] = createSignal<{
    parentId: string | null;
    node?: SiteNode;
  }>();
  const [pointDialog, setPointDialog] = createSignal<{
    nodeId: string;
    point?: MeasurementPoint;
  }>();
  const [placing, setPlacing] = createSignal<{
    nodeId?: string;
    device?: DeviceInfo;
  }>();
  const [collapsed, setCollapsed] = createSignal<ReadonlySet<string>>(
    new Set(),
  );
  const [view, setView] = createSignal<"tree" | "list">("tree");
  const [query, setQuery] = createSignal("");
  const [showFilters, setShowFilters] = createSignal(false);
  const [filter, setFilter] = createSignal<Filter>(viewFilter("all"));
  const [showPoints, setShowPoints] = createSignal(true);
  const [whole, setWhole] = createSignal(false);
  const [dropOn, setDropOn] = createSignal<string>();

  const nodes = () => siteTree()?.nodes ?? [];
  const points = () => siteTree()?.points ?? [];
  const byId = createMemo(() => new Map(nodes().map((n) => [n.id, n])));

  // The part of the tree shown: the scope (and everything in it), or the whole tree on request.
  const rootId = () => (whole() ? null : (scope()?.id ?? null));
  const inside = createMemo(() =>
    rootId() === null
      ? new Set(nodes().map((n) => n.id))
      : new Set(subtreeOf(nodes(), rootId()!)),
  );

  // Each root's points now, merged: the tree, the list and the node table show the same values.
  const roots = () =>
    nodes()
      .filter((n) => n.parentId === null)
      .map((n) => n.id);
  const [readings, { refetch: refetchReadings }] = createResource(
    () => (siteTree() ? roots().join() : undefined),
    async () =>
      new Map(
        (await Promise.all(roots().map((id) => api.sites.pointsNow(id))))
          .flat()
          .map((r) => [r.pointId, r]),
      ),
  );
  const reading = (id: string) => readings()?.get(id);
  const [devices, { refetch: refetchDevices }] = createResource(() =>
    api.devices.list(),
  );
  const unassigned = () =>
    devices() ? unassignedDevices(devices()!, points()) : [];

  const path = (nodeId: string) =>
    pathTo(nodes(), nodeId)
      .map((n) => n.name)
      .join(" / ");
  const now = () => Date.now();
  const stateOf = (p: MeasurementPoint): PointState =>
    pointState(p, reading(p.id), now());
  const scoped = createMemo(() =>
    points().filter((p) => inside().has(p.nodeId)),
  );
  const passing = createMemo(() =>
    scoped().filter((p) =>
      passes(p, stateOf(p), filter(), query(), path(p.nodeId)),
    ),
  );
  const filtered = () => isFiltered(filter(), query());
  const shownNodes = createMemo(() => {
    const keep = visibleNodes(nodes(), passing(), query(), filtered());
    return new Set([...keep].filter((id) => inside().has(id)));
  });

  const current = (): Selection | undefined =>
    selection() ?? (scope() ? { kind: "node", id: scope()!.id } : undefined);
  const selectedNode = () => {
    const s = current();
    return s?.kind === "node" ? byId().get(s.id) : undefined;
  };
  const selectedPoint = () => {
    const s = current();
    return s?.kind === "point"
      ? points().find((p) => p.id === s.id)
      : undefined;
  };

  // Rows hidden under a collapsed ancestor or by the filter are skipped; the walk starts at the shown root.
  const rows = createMemo(() => {
    const hidden = new Set<string>();
    const top = rootId();
    const base =
      top === null
        ? 0
        : (depthFirst(nodes()).find((r) => r.node.id === top)?.depth ?? 0);
    return depthFirst(nodes())
      .filter(({ node }) => {
        if (!shownNodes().has(node.id)) {
          return false;
        }
        if (
          node.id !== top &&
          node.parentId !== null &&
          (hidden.has(node.parentId) || collapsed().has(node.parentId))
        ) {
          hidden.add(node.id);
          return false;
        }
        return true;
      })
      .map((r) => ({ ...r, depth: r.depth - base }));
  });
  const toggle = (id: string) => {
    const next = new Set(collapsed());
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setCollapsed(next);
  };
  const setMedium = (m: string) => {
    const media = new Set(filter().media);
    if (media.has(m)) {
      media.delete(m);
    } else {
      media.add(m);
    }
    setFilter({ ...filter(), media });
  };
  const filterCount = () =>
    filter().media.size + (filter().state !== "all" ? 1 : 0);
  const refresh = async () => {
    await reloadTree();
    await Promise.all([refetchReadings(), refetchDevices()]);
  };

  const pointRow = (p: MeasurementPoint) => {
    const r = () => reading(p.id);
    const data = () => dataState(p, r(), now());
    return { r, data };
  };

  return (
    <>
      <PageHeader
        eyebrow={t("assets.eyebrow")}
        title={t("assets.title")}
        sub={t("assets.intro")}
      >
        <div class="seg" role="group" aria-label={t("assets.viewLabel")}>
          <button
            type="button"
            aria-pressed={view() === "tree"}
            onClick={() => setView("tree")}
          >
            <Icon name="tree" /> {t("assets.viewTree")}
          </button>
          <button
            type="button"
            aria-pressed={view() === "list"}
            onClick={() => setView("list")}
          >
            <Icon name="list" /> {t("assets.viewList")}
          </button>
        </div>
        <Show when={operator()}>
          <button
            type="button"
            class="btn"
            onClick={() =>
              setNodeDialog({ parentId: selectedNode()?.id ?? null })
            }
          >
            <Icon name="plus" />
            {t("assets.addNode")}
          </button>
          <Show
            when={
              selectedNode() ??
              (selectedPoint() && byId().get(selectedPoint()!.nodeId))
            }
          >
            {(n) => (
              <button
                type="button"
                class="btn"
                onClick={() => setPointDialog({ nodeId: n().id })}
              >
                <Icon name="gauge" />
                {t("assets.newPoint")}
              </button>
            )}
          </Show>
        </Show>
      </PageHeader>

      <Show
        when={!siteTree.error}
        fallback={<ErrorBox error={siteTree.error} />}
      >
        <Show
          when={siteTree()}
          fallback={
            <section class="card">
              <Skeleton />
            </section>
          }
        >
          <Show
            when={nodes().length > 0}
            fallback={
              <section class="card empty">
                <h2>{t("assets.emptyTitle")}</h2>
                <p class="muted">{t("assets.empty")}</p>
                <Show when={operator()}>
                  <div>
                    <button
                      type="button"
                      class="btn primary"
                      onClick={() => setNodeDialog({ parentId: null })}
                    >
                      {t("assets.addFirst")}
                    </button>
                  </div>
                </Show>
              </section>
            }
          >
            <div class={`res ${view() === "list" ? "list" : ""}`}>
              <section class="card" aria-label={t("assets.title")}>
                <div class="panel-h">
                  <label class="search">
                    <Icon name="search" />
                    <input
                      id="assets-q"
                      type="search"
                      placeholder={t("assets.searchPoints")}
                      value={query()}
                      autocomplete="off"
                      onInput={(e) => setQuery(e.currentTarget.value)}
                    />
                  </label>
                  <button
                    type="button"
                    class="btn ghost sm"
                    aria-pressed={showFilters()}
                    onClick={() => setShowFilters(!showFilters())}
                  >
                    <Icon name="filter" />
                    {t("assets.filters")}
                    <Show when={filterCount() > 0}>
                      <span class="chip acc">{filterCount()}</span>
                    </Show>
                  </button>
                  <Show when={view() === "tree"}>
                    <button
                      type="button"
                      class="btn ghost sm"
                      title={t("assets.expandAll")}
                      aria-label={t("assets.expandAll")}
                      onClick={() => setCollapsed(new Set())}
                    >
                      <Icon name="expand" />
                    </button>
                    <button
                      type="button"
                      class="btn ghost sm"
                      title={t("assets.collapseAll")}
                      aria-label={t("assets.collapseAll")}
                      onClick={() =>
                        setCollapsed(
                          new Set(
                            nodes()
                              .map((n) => n.id)
                              .filter((id) => id !== rootId()),
                          ),
                        )
                      }
                    >
                      <Icon name="collapse" />
                    </button>
                    <button
                      type="button"
                      class="btn ghost sm"
                      aria-pressed={showPoints()}
                      onClick={() => setShowPoints(!showPoints())}
                    >
                      <Icon name="gauge" />
                      {showPoints()
                        ? t("assets.withPoints")
                        : t("assets.nodesOnly")}
                    </button>
                  </Show>
                </div>
                <Show when={showFilters()}>
                  <div class="fbar">
                    <div class="frow">
                      <span class="eyebrow">{t("assets.views")}</span>
                      <div class="views">
                        <For each={VIEWS}>
                          {(v) => (
                            <button
                              type="button"
                              class="vchip"
                              aria-pressed={
                                sameFilter(filter(), viewFilter(v)) &&
                                query() === ""
                              }
                              onClick={() => setFilter(viewFilter(v))}
                            >
                              {t(`assets.view.${v}`)}
                            </button>
                          )}
                        </For>
                      </div>
                    </div>
                    <div class="frow">
                      <span class="eyebrow">{t("assets.medium")}</span>
                      <div class="views">
                        <For each={MEDIA_IDS}>
                          {(m) => (
                            <button
                              type="button"
                              class="mchip"
                              aria-pressed={filter().media.has(m)}
                              onClick={() => setMedium(m)}
                            >
                              <MediumBadge medium={m} />
                              {t(`medium.${m}`)}
                            </button>
                          )}
                        </For>
                      </div>
                    </div>
                    <div class="frow">
                      <span class="eyebrow">{t("assets.state")}</span>
                      <div class="views">
                        <For each={["all", "attn", "pre", "ok"] as const}>
                          {(s) => (
                            <button
                              type="button"
                              class="mchip plain"
                              aria-pressed={filter().state === s}
                              onClick={() =>
                                setFilter({ ...filter(), state: s })
                              }
                            >
                              {t(`assets.pointState.${s}`)}
                            </button>
                          )}
                        </For>
                      </div>
                    </div>
                  </div>
                </Show>
                <div class="scopebar">
                  <Icon name="target" />
                  <span>
                    {t("assets.scope")}:{" "}
                    <b style={{ "font-weight": 500, color: "var(--ink)" }}>
                      {whole() ? t("assets.wholeTree") : (scope()?.name ?? "")}
                    </b>
                  </span>
                  <Show when={scope()?.parentId !== null || roots().length > 1}>
                    <button
                      type="button"
                      class="btn ghost sm"
                      aria-pressed={whole()}
                      onClick={() => setWhole(!whole())}
                    >
                      {whole() ? t("assets.onlyScope") : t("assets.showWhole")}
                    </button>
                  </Show>
                  <span style={{ "margin-left": "auto" }}>
                    <Show
                      when={filtered()}
                      fallback={t("assets.pointsTotal", {
                        count: scoped().length,
                      })}
                    >
                      {t("assets.pointsShown", {
                        shown: passing().length,
                        count: scoped().length,
                      })}{" "}
                      ·{" "}
                      <button
                        type="button"
                        class="btn ghost sm"
                        onClick={() => {
                          setFilter(viewFilter("all"));
                          setQuery("");
                        }}
                      >
                        {t("common.clear")}
                      </button>
                    </Show>
                  </span>
                </div>
                <Show
                  when={view() === "tree"}
                  fallback={
                    <PointList
                      points={passing()}
                      reading={reading}
                      path={path}
                      selected={selectedPoint()?.id}
                      onSelect={(id) => select({ kind: "point", id })}
                    />
                  }
                >
                  <ul class="tree" role="tree" aria-label={t("assets.title")}>
                    <For
                      each={rows()}
                      fallback={
                        <li class="hint" style={{ padding: "10px" }}>
                          {t("assets.noMatch")}
                        </li>
                      }
                    >
                      {({ node, depth }) => {
                        const own = () =>
                          passing().filter((p) => p.nodeId === node.id);
                        const total = () =>
                          scoped().filter((p) =>
                            subtreeOf(nodes(), node.id).includes(p.nodeId),
                          );
                        const attention = () =>
                          total().filter((p) => stateOf(p) === "attn").length;
                        const hasChildren = () =>
                          nodes().some((n) => n.parentId === node.id) ||
                          own().length > 0;
                        const open = () => !collapsed().has(node.id);
                        const drop = {
                          onDragOver: (e: DragEvent) => {
                            if (
                              e.dataTransfer?.types.includes(
                                "application/x-ep-device",
                              )
                            ) {
                              e.preventDefault();
                              setDropOn(node.id);
                            }
                          },
                          onDragLeave: () => setDropOn(undefined),
                          onDrop: (e: DragEvent) => {
                            e.preventDefault();
                            setDropOn(undefined);
                            const device = devices()?.find(
                              (d) =>
                                d.id ===
                                e.dataTransfer?.getData(
                                  "application/x-ep-device",
                                ),
                            );
                            if (device) {
                              setPlacing({ nodeId: node.id, device });
                            }
                          },
                        };
                        return (
                          <li
                            role="treeitem"
                            aria-expanded={hasChildren() ? open() : undefined}
                          >
                            <div
                              class={`row node ${current()?.kind === "node" && current()?.id === node.id ? "sel" : ""} ${scope()?.id === node.id ? "scope" : ""} ${dropOn() === node.id ? "drop" : ""}`}
                              style={{ "padding-left": `${4 + depth * 16}px` }}
                              {...drop}
                            >
                              <button
                                type="button"
                                class={`tw ${hasChildren() ? "" : "leaf"}`}
                                aria-expanded={open()}
                                onClick={() => toggle(node.id)}
                              >
                                <Icon name="chev" />
                                <span class="sr-only">
                                  {t("common.toggle")}
                                </span>
                              </button>
                              <span class="ico">
                                <NodeKindIcon kind={node.kind} />
                              </span>
                              <button
                                type="button"
                                class="nm"
                                onClick={() =>
                                  select({ kind: "node", id: node.id })
                                }
                              >
                                {node.name}
                              </button>
                              <Show when={attention() > 0}>
                                <span
                                  class="dot warn"
                                  title={t("assets.attentionCount", {
                                    count: attention(),
                                  })}
                                />
                              </Show>
                              <span class="cnt">{total().length || ""}</span>
                            </div>
                            <Show when={open() && showPoints()}>
                              <For each={own()}>
                                {(p) => {
                                  const { r, data } = pointRow(p);
                                  return (
                                    <div
                                      class={`row pt ${current()?.kind === "point" && current()?.id === p.id ? "sel" : ""} ${p.readiness === "project" ? "proj" : ""} ${data() === "late" || data() === "none" ? "stale" : ""}`}
                                      style={{
                                        "padding-left": `${26 + (depth + 1) * 16}px`,
                                      }}
                                    >
                                      <MediumBadge medium={p.medium} />
                                      <button
                                        type="button"
                                        class="nm"
                                        onClick={() =>
                                          select({ kind: "point", id: p.id })
                                        }
                                      >
                                        {p.name}
                                      </button>
                                      <Show when={p.readiness !== "running"}>
                                        <span
                                          class={`chip ${READINESS_CHIP[p.readiness] ?? ""}`}
                                        >
                                          {t(`readiness.${p.readiness}`)}
                                        </span>
                                      </Show>
                                      <span class="v">
                                        {p.binding ? (
                                          <ReadingValue reading={r()} />
                                        ) : (
                                          t("assets.unbound")
                                        )}
                                      </span>
                                      <Show
                                        when={
                                          data() === "late" || data() === "none"
                                        }
                                      >
                                        <span
                                          class={`dot ${data() === "none" ? "crit" : "warn"}`}
                                          title={
                                            data() === "late"
                                              ? t("overview.lateSince", {
                                                  ago: formatAgo(
                                                    r()?.at,
                                                    locale(),
                                                  ),
                                                })
                                              : t("overview.noReading")
                                          }
                                        />
                                      </Show>
                                    </div>
                                  );
                                }}
                              </For>
                            </Show>
                          </li>
                        );
                      }}
                    </For>
                  </ul>
                  <Show when={operator()}>
                    <div class="inbox">
                      <div class="inbox-h">
                        <Icon name="inbox" />
                        <b>{t("assets.unassigned")}</b>
                        <span class="chip acc">{unassigned().length}</span>
                        <span class="hint" style={{ "margin-left": "auto" }}>
                          {t("assets.fromIntegrations")}
                        </span>
                      </div>
                      <Show
                        when={unassigned().length > 0}
                        fallback={
                          <div
                            class="hint"
                            style={{ padding: "4px 12px 10px" }}
                          >
                            {t("assets.allAssigned")}
                          </div>
                        }
                      >
                        <div class="hint" style={{ padding: "0 12px 4px" }}>
                          {t("assets.dragHint")}
                        </div>
                        <For each={unassigned()}>
                          {(d) => (
                            <div
                              class="dev"
                              draggable="true"
                              tabindex="0"
                              role="button"
                              aria-label={t("assets.placeDevice", {
                                name: d.name,
                              })}
                              onDragStart={(e) =>
                                e.dataTransfer?.setData(
                                  "application/x-ep-device",
                                  d.id,
                                )
                              }
                              onClick={() =>
                                setPlacing({
                                  nodeId:
                                    selectedNode()?.id ??
                                    selectedPoint()?.nodeId,
                                  device: d,
                                })
                              }
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  setPlacing({
                                    nodeId:
                                      selectedNode()?.id ??
                                      selectedPoint()?.nodeId,
                                    device: d,
                                  });
                                }
                              }}
                            >
                              <Icon name="plug" />
                              <div style={{ "min-width": 0 }}>
                                <b style={{ "font-weight": 500 }}>{d.name}</b>
                                <small>{t(`category.${d.category}`)}</small>
                              </div>
                            </div>
                          )}
                        </For>
                      </Show>
                    </div>
                  </Show>
                </Show>
              </section>

              <Show when={selectedNode()} keyed>
                {(n) => (
                  <NodeDetail
                    node={n}
                    points={scoped().filter((p) =>
                      subtreeOf(nodes(), n.id).includes(p.nodeId),
                    )}
                    reading={reading}
                    onAddChild={() => setNodeDialog({ parentId: n.id })}
                    onEdit={() =>
                      setNodeDialog({ parentId: n.parentId, node: n })
                    }
                    onAddPoint={() => setPointDialog({ nodeId: n.id })}
                    onPlace={() => setPlacing({ nodeId: n.id })}
                    onSelectPoint={(id) => select({ kind: "point", id })}
                    onDeleted={() => select(undefined)}
                  />
                )}
              </Show>
              <Show when={selectedPoint()} keyed>
                {(p) => (
                  <PointCard
                    point={p}
                    reading={reading(p.id)}
                    onEdit={() =>
                      setPointDialog({ nodeId: p.nodeId, point: p })
                    }
                    onChanged={refresh}
                    onDeleted={() => select({ kind: "node", id: p.nodeId })}
                    onSelectNode={(id) => select({ kind: "node", id })}
                  />
                )}
              </Show>
            </div>
          </Show>
        </Show>
      </Show>

      <Show when={nodeDialog()}>
        {(d) => (
          <NodeDialog
            parentId={d().parentId}
            node={d().node}
            onClose={() => setNodeDialog(undefined)}
            onSaved={(n) => {
              select({ kind: "node", id: n.id });
              if (!scope()) {
                setScope(n.id);
              }
            }}
          />
        )}
      </Show>
      <Show when={pointDialog()}>
        {(d) => (
          <PointDialog
            nodeId={d().nodeId}
            point={d().point}
            onClose={() => setPointDialog(undefined)}
            onSaved={(p) => select({ kind: "point", id: p.id })}
          />
        )}
      </Show>
      <Show when={placing()}>
        {(pl) => (
          <PlaceDeviceDialog
            nodeId={pl().nodeId}
            device={pl().device}
            onClose={() => setPlacing(undefined)}
            onSaved={(p) => {
              void refresh();
              select({ kind: "point", id: p.id });
            }}
          />
        )}
      </Show>
    </>
  );
}

/** Points as a table with their place, readiness, data state and value now (prototype "Lista punktów"). */
function PointList(props: {
  points: MeasurementPoint[];
  reading: (id: string) => PointReading | undefined;
  path: (nodeId: string) => string;
  selected?: string;
  onSelect: (id: string) => void;
  showPath?: boolean;
}) {
  return (
    <div class="scrollx">
      <table class="tbl">
        <thead>
          <tr>
            <th />
            <th>{t("assets.point")}</th>
            <th>{t("assets.node")}</th>
            <th>{t("assets.readiness")}</th>
            <th>{t("assets.data")}</th>
            <th class="r">{t("assets.now")}</th>
          </tr>
        </thead>
        <tbody>
          <For
            each={props.points}
            fallback={
              <tr>
                <td colspan="6" class="hint">
                  {t("assets.noMatch")}
                </td>
              </tr>
            }
          >
            {(p) => (
              <tr
                class={`click ${props.selected === p.id ? "sel" : ""}`}
                onClick={() => props.onSelect(p.id)}
              >
                <td style={{ width: "24px" }}>
                  <MediumBadge medium={p.medium} />
                </td>
                <td>{p.name}</td>
                <td>
                  <div class="path">{props.path(p.nodeId)}</div>
                </td>
                <td>
                  <span class={`chip ${READINESS_CHIP[p.readiness] ?? ""}`}>
                    {t(`readiness.${p.readiness}`)}
                  </span>
                </td>
                <td>
                  <DataChip point={p} reading={props.reading(p.id)} />
                </td>
                <td class="r num">
                  <ReadingValue reading={props.reading(p.id)} />
                </td>
              </tr>
            )}
          </For>
        </tbody>
      </table>
    </div>
  );
}

function NodeDetail(props: {
  node: SiteNode;
  points: MeasurementPoint[];
  reading: (id: string) => PointReading | undefined;
  onAddChild: () => void;
  onEdit: () => void;
  onAddPoint: () => void;
  onPlace: () => void;
  onSelectPoint: (id: string) => void;
  onDeleted: () => void;
}) {
  const [remove, setRemove] = createSignal(false);
  const parent = () =>
    siteTree()?.nodes.find((n) => n.id === props.node.parentId);
  const children = () =>
    (siteTree()?.nodes ?? []).filter((n) => n.parentId === props.node.id);
  const mains = () => props.points.filter((p) => p.role === "main");
  const crumbs = () =>
    pathTo(siteTree()?.nodes ?? [], props.node.id)
      .slice(0, -1)
      .map((n) => n.name)
      .join(" / ");
  const nodeName = (id: string) =>
    siteTree()?.nodes.find((n) => n.id === id)?.name ?? "";
  const bound = () => props.points.filter((p) => p.binding);
  const current = () =>
    bound().filter(
      (p) => dataState(p, props.reading(p.id), Date.now()) === "ok",
    );
  const beforeAcceptance = () =>
    props.points.filter(
      (p) =>
        p.readiness === "project" ||
        p.readiness === "config" ||
        p.readiness === "check",
    );

  return (
    <section class="card" id="detail">
      <div class="card-h">
        <span class="ico">
          <NodeKindIcon kind={props.node.kind} />
        </span>
        <div style={{ "min-width": 0 }}>
          <div class="eyebrow">
            {t(`nodeKind.${props.node.kind}`)} · {crumbs() || t("assets.root")}
          </div>
          <h2 style={{ "font-size": "16px" }}>{props.node.name}</h2>
        </div>
        <div
          style={{
            "margin-left": "auto",
            display: "flex",
            gap: "6px",
            "flex-wrap": "wrap",
          }}
        >
          <Show when={scope()?.id !== props.node.id}>
            <button
              type="button"
              class="btn"
              onClick={() => setScope(props.node.id)}
            >
              <Icon name="target" />
              {t("assets.setScope")}
            </button>
          </Show>
          <A
            class="btn"
            href="/overview"
            onClick={() => setScope(props.node.id)}
          >
            <Icon name="chart" />
            {t("overview.title")}
          </A>
          <Show when={operator()}>
            <button type="button" class="btn" onClick={() => props.onEdit()}>
              <Icon name="edit" />
              {t("common.edit")}
            </button>
            <button
              type="button"
              class="btn"
              onClick={() => props.onAddChild()}
            >
              <Icon name="plus" />
              {t("assets.addChild")}
            </button>
          </Show>
        </div>
      </div>
      <Show when={operator()}>
        <div class="panel-h">
          <button
            type="button"
            class="btn sm primary"
            onClick={() => props.onPlace()}
          >
            <Icon name="link" />
            {t("assets.bindDevice")}
          </button>
          <button
            type="button"
            class="btn sm"
            onClick={() => props.onAddPoint()}
          >
            <Icon name="gauge" />
            {t("assets.addPoint")}
          </button>
          <button
            type="button"
            class="btn sm danger"
            style={{ "margin-left": "auto" }}
            onClick={() => setRemove(true)}
          >
            {t("common.delete")}
          </button>
        </div>
      </Show>
      <div class="card-b stack">
        <div class="axes">
          <div class="axis">
            <span class="eyebrow">{t("assets.points")}</span>
            <span class="num" style={{ "font-size": "18px" }}>
              {props.points.length}
            </span>
            <span class="q">
              {t("assets.beforeAcceptance", {
                count: beforeAcceptance().length,
              })}
            </span>
          </div>
          <div class="axis">
            <span class="eyebrow">{t("assets.freshness")}</span>
            <span class="num" style={{ "font-size": "18px" }}>
              {current().length}/{bound().length}
            </span>
            <span class="q">{t("assets.currentOf")}</span>
          </div>
          <div class="axis">
            <span class="eyebrow">{t("assets.boundary")}</span>
            <span style={{ "font-weight": 500 }}>
              {mains().length > 0
                ? mains()
                    .map((p) => p.name)
                    .join(", ")
                : t("assets.notConfigured")}
            </span>
            <span class="q">
              <span class={`chip ${mains().length > 0 ? "ok" : ""}`}>
                {mains().length > 0
                  ? t("assets.balanceAvailable")
                  : t("assets.noSum")}
              </span>
            </span>
          </div>
        </div>
        <Show when={mains().length === 0}>
          <p class="hint" style={{ margin: 0 }}>
            {t("assets.noBoundary")}
          </p>
        </Show>
      </div>
      <Show when={props.points.length > 0}>
        <div class="scrollx">
          <table class="tbl">
            <thead>
              <tr>
                <th />
                <th>{t("assets.point")}</th>
                <th>{t("assets.node")}</th>
                <th>{t("assets.readiness")}</th>
                <th>{t("assets.data")}</th>
                <th class="r">{t("assets.now")}</th>
              </tr>
            </thead>
            <tbody>
              <For each={props.points}>
                {(p) => (
                  <tr class="click" onClick={() => props.onSelectPoint(p.id)}>
                    <td style={{ width: "24px" }}>
                      <MediumBadge medium={p.medium} />
                    </td>
                    <td>
                      {p.name}
                      <div class="hint">{t(`role.point.${p.role}`)}</div>
                    </td>
                    <td class="hint">{nodeName(p.nodeId)}</td>
                    <td>
                      <span class={`chip ${READINESS_CHIP[p.readiness] ?? ""}`}>
                        {t(`readiness.${p.readiness}`)}
                      </span>
                    </td>
                    <td>
                      <DataChip point={p} reading={props.reading(p.id)} />
                    </td>
                    <td class="r num">
                      <ReadingValue reading={props.reading(p.id)} />
                    </td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </div>
      </Show>
      <div class="card-b">
        <dl class="kv">
          <dt>{t("assets.parent")}</dt>
          <dd>{parent()?.name ?? t("assets.root")}</dd>
          <dt>{t("assets.timeZone")}</dt>
          <dd class="mono">{props.node.timeZone}</dd>
          <Show
            when={props.node.latitude !== null && props.node.longitude !== null}
          >
            <dt>{t("assets.location")}</dt>
            <dd class="mono">
              {props.node.latitude}, {props.node.longitude}
            </dd>
          </Show>
          <dt>{t("assets.children")}</dt>
          <dd>{children().length}</dd>
        </dl>
      </div>
      <Show when={remove()}>
        <ConfirmDialog
          title={t("assets.deleteNodeTitle", { name: props.node.name })}
          text={t("assets.deleteNodeText")}
          confirm={t("common.delete")}
          danger
          onConfirm={async () => {
            await api.sites.removeNode(props.node.id);
            await reloadTree();
            props.onDeleted();
          }}
          onClose={() => setRemove(false)}
        />
      </Show>
    </section>
  );
}

/** Adds a place or edits one: name, kind, parent, time zone and where it is on the map. */
export function NodeDialog(props: {
  parentId: string | null;
  node?: SiteNode;
  onClose: () => void;
  onSaved: (n: SiteNode) => void;
}) {
  const [name, setName] = createSignal(props.node?.name ?? "");
  const [kind, setKind] = createSignal(
    props.node?.kind ?? (props.parentId === null ? "site" : "building"),
  );
  const [parentId, setParentId] = createSignal<string | null>(props.parentId);
  const [timeZone, setTimeZone] = createSignal(
    props.node?.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
  );
  const [position, setPosition] = createSignal<LatLon | null>(
    props.node?.latitude != null && props.node.longitude != null
      ? { latitude: props.node.latitude, longitude: props.node.longitude }
      : null,
  );
  const [run, error, busy] = useAction();

  const save = (e: SubmitEvent) => {
    e.preventDefault();
    void run(async () => {
      const input = {
        parentId: parentId(),
        name: name().trim(),
        kind: kind(),
        timeZone: timeZone(),
        latitude: position()?.latitude ?? null,
        longitude: position()?.longitude ?? null,
      };
      const saved = props.node
        ? await api.sites.updateNode(props.node.id, input)
        : await api.sites.createNode(input);
      await reloadTree();
      props.onSaved(saved);
      props.onClose();
    });
  };

  return (
    <Dialog
      title={props.node ? t("assets.editNode") : t("assets.addNode")}
      onClose={props.onClose}
      wide
    >
      <form class="stack" onSubmit={save}>
        <div class="field">
          <label for="siteName">{t("common.name")}</label>
          <input
            id="siteName"
            required
            maxLength={120}
            value={name()}
            autofocus
            onInput={(e) => setName(e.currentTarget.value)}
          />
        </div>
        <div class="field">
          <label for="nodeKind">{t("assets.kind")}</label>
          <select
            id="nodeKind"
            value={kind()}
            onChange={(e) => setKind(e.currentTarget.value)}
          >
            <For each={NODE_KINDS}>
              {(k) => <option value={k}>{t(`nodeKind.${k}`)}</option>}
            </For>
          </select>
        </div>
        <div class="field">
          <label for="nodeParent">{t("assets.parent")}</label>
          <select
            id="nodeParent"
            value={parentId() ?? ""}
            onChange={(e) => setParentId(e.currentTarget.value || null)}
          >
            <option value="">{t("assets.root")}</option>
            <For
              each={depthFirst(siteTree()?.nodes ?? []).filter(
                ({ node }) => node.id !== props.node?.id,
              )}
            >
              {({ node, depth }) => (
                <option
                  value={node.id}
                >{`${"  ".repeat(depth)}${node.name}`}</option>
              )}
            </For>
          </select>
        </div>
        <div class="field">
          <label for="nodeTz">{t("assets.timeZone")}</label>
          <input
            id="nodeTz"
            value={timeZone()}
            onInput={(e) => setTimeZone(e.currentTarget.value)}
          />
          <p class="hint">{t("assets.timeZoneHint")}</p>
        </div>
        <div class="field">
          <label for="placeSearch">{t("assets.location")}</label>
          <LocationPicker value={position()} onChange={setPosition} />
        </div>
        <Show when={error()}>
          <ErrorBox error={error()} />
        </Show>
        <div class="row-acts" style={{ "justify-content": "flex-end" }}>
          <button type="button" class="btn" onClick={() => props.onClose()}>
            {t("common.cancel")}
          </button>
          <button type="submit" class="btn primary" disabled={busy()}>
            {t("common.save")}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

function PointDialog(props: {
  nodeId: string;
  point?: MeasurementPoint;
  onClose: () => void;
  onSaved: (p: MeasurementPoint) => void;
}) {
  const [name, setName] = createSignal(props.point?.name ?? "");
  const [medium, setMedium] = createSignal(props.point?.medium ?? "el");
  const [role, setRole] = createSignal(props.point?.role ?? "main");
  const [readiness, setReadiness] = createSignal(
    props.point?.readiness ?? "config",
  );
  const [nodeId, setNodeId] = createSignal(props.nodeId);
  const [run, error, busy] = useAction();

  const save = (e: SubmitEvent) => {
    e.preventDefault();
    void run(async () => {
      const saved = props.point
        ? await api.sites.updatePoint(props.point.id, {
            nodeId: nodeId(),
            name: name().trim(),
            medium: medium(),
            role: role(),
            readiness: readiness(),
          })
        : await api.sites.createPoint(
            nodeId(),
            name().trim(),
            medium(),
            role(),
          );
      await reloadTree();
      props.onSaved(saved);
      props.onClose();
    });
  };

  return (
    <Dialog
      title={props.point ? t("assets.editPoint") : t("assets.addPoint")}
      onClose={props.onClose}
    >
      <form class="stack" onSubmit={save}>
        <div class="field">
          <label for="ptName">{t("common.name")}</label>
          <input
            id="ptName"
            required
            maxLength={120}
            value={name()}
            autofocus
            onInput={(e) => setName(e.currentTarget.value)}
          />
        </div>
        <div class="field">
          <label for="ptNode">{t("assets.node")}</label>
          <select
            id="ptNode"
            value={nodeId()}
            onChange={(e) => setNodeId(e.currentTarget.value)}
          >
            <For each={depthFirst(siteTree()?.nodes ?? [])}>
              {({ node, depth }) => (
                <option
                  value={node.id}
                >{`${"  ".repeat(depth)}${node.name}`}</option>
              )}
            </For>
          </select>
        </div>
        <div class="field">
          <label>{t("assets.medium")}</label>
          <div class="frow">
            <For each={MEDIA_IDS}>
              {(m) => (
                <button
                  type="button"
                  class="mchip"
                  aria-pressed={medium() === m}
                  onClick={() => setMedium(m)}
                >
                  <MediumBadge medium={m} />
                  {t(`medium.${m}`)}
                </button>
              )}
            </For>
          </div>
        </div>
        <div class="field">
          <label for="ptRole">{t("assets.role")}</label>
          <select
            id="ptRole"
            value={role()}
            onChange={(e) => setRole(e.currentTarget.value)}
          >
            <For each={ROLES}>
              {(r) => <option value={r}>{t(`role.point.${r}`)}</option>}
            </For>
          </select>
          <p class="hint">{t(`role.pointHint.${role()}`)}</p>
        </div>
        <Show when={props.point}>
          <div class="field">
            <label for="ptReady">{t("assets.readiness")}</label>
            <select
              id="ptReady"
              value={readiness()}
              onChange={(e) => setReadiness(e.currentTarget.value)}
            >
              <For each={READINESS}>
                {(r) => <option value={r}>{t(`readiness.${r}`)}</option>}
              </For>
            </select>
          </div>
        </Show>
        <Show when={error()}>
          <ErrorBox error={error()} />
        </Show>
        <div class="row-acts" style={{ "justify-content": "flex-end" }}>
          <button type="button" class="btn" onClick={() => props.onClose()}>
            {t("common.cancel")}
          </button>
          <button type="submit" class="btn primary" disabled={busy()}>
            {t("common.save")}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
